import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Avatar from '@mui/material/Avatar';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import {
  useListMyTicketsQuery,
  useGetMyTicketQuery,
  useReplyToTicketMutation,
} from '../store/supportApi';
import { MyTicketsShimmer } from '../components/shimmer';
import { useAuth } from '../context/AuthContext';
import { useUI } from '../context/UIContext';
import { ROUTES } from '../constants/routes';

// The five stages Trust OPM tracks (opm_status), as the customer sees them.
// The portal shows the same labels for the same stages.
const STATUS_TONE = {
  open:        { bg: '#FEF3C7', fg: '#92400E', label: 'Received' },
  assigned:    { bg: '#DBEAFE', fg: '#1E40AF', label: 'With department' },
  in_progress: { bg: '#DBEAFE', fg: '#1E40AF', label: 'In progress' },
  resolved:    { bg: '#D1FAE5', fg: '#065F46', label: 'Resolved' },
  closed:      { bg: 'var(--border-l)', fg: 'var(--stone)', label: 'Closed' },
};
const stageOf = (t) => t?.opm_status || t?.status || 'open';
const STAGE_INDEX = { open: 0, assigned: 1, in_progress: 2, resolved: 3, closed: 3 };
const STEPS = ['Received', 'With department', 'In progress', 'Resolved'];

// The small horizontal stepper at the top of the dialog.
const Stepper = ({ stage }) => {
  const at = STAGE_INDEX[stage] ?? 0;
  return (
    <Stack direction="row" alignItems="center" sx={{ mb: 2, overflowX: 'auto' }}>
      {STEPS.map((label, i) => {
        const done = i <= at;
        const current = i === at;
        return (
          <Stack key={label} direction="row" alignItems="center" sx={{ flexShrink: 0 }}>
            <Stack alignItems="center" spacing={0.5} sx={{ minWidth: 92 }}>
              <Box sx={{
                width: 26, height: 26, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: done ? 'var(--sage)' : '#fff',
                color: done ? '#fff' : 'var(--stone)',
                border: `2px solid ${current ? 'var(--ink)' : done ? 'var(--sage)' : 'var(--border-l)'}`,
                fontSize: 11, fontWeight: 700,
              }}>
                {done ? '✓' : i + 1}
              </Box>
              <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: done ? 'var(--ink)' : 'var(--stone)', textAlign: 'center' }}>
                {label}
              </Typography>
            </Stack>
            {i < STEPS.length - 1 && (
              <Box sx={{ width: 28, height: 2, bgcolor: i < at ? 'var(--sage)' : 'var(--border-l)', mb: 2.5 }} />
            )}
          </Stack>
        );
      })}
    </Stack>
  );
};

const Fact = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--stone)', textTransform: 'uppercase' }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', wordBreak: 'break-word' }}>
      {value || '—'}
    </Typography>
  </Box>
);

const CATEGORY_LABEL = {
  trustian: 'Trustian', report: 'Report', booking: 'Booking', sample: 'Sample',
  app: 'App', billing: 'Billing', membership: 'Membership', other: 'Other',
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true });
};

const Pill = ({ tone }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex', alignItems: 'center', width: 'fit-content',
      bgcolor: tone?.bg || 'var(--border-l)', color: tone?.fg || 'var(--stone)',
      fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
      px: 1.25, py: 0.5, borderRadius: 100, whiteSpace: 'nowrap',
    }}
  >
    {tone?.label}
  </Box>
);

// ───────── Detail dialog with the full thread + user reply ─────────
const TicketDialog = ({ ticketId, open, onClose }) => {
  const { data, isLoading, refetch } = useGetMyTicketQuery(ticketId, { skip: !ticketId });
  const ticket = data?.ticket;
  const { showToast } = useUI();
  const [reply, { isLoading: sending }] = useReplyToTicketMutation();
  const [text, setText] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => { setText(''); setErr(''); }, [ticketId]);

  const onSend = async () => {
    setErr('');
    if (!text.trim()) return setErr('Please type a reply');
    try {
      await reply({ id: ticket.ticket_id, message: text.trim() }).unwrap();
      setText('');
      showToast('Reply sent');
      refetch();
    } catch (e) {
      setErr(e?.data?.message || 'Could not send reply');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px', maxHeight: '90vh' } }}
    >
      <DialogTitle sx={{ pb: 1, pr: 6 }}>
        <Typography component="div" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--sage)' }}>
          Support ticket
        </Typography>
        <Typography
          sx={{
            fontSize: 16, fontWeight: 700, color: 'var(--ink)',
            fontFamily: "'JetBrains Mono', monospace", mt: 0.5,
          }}
        >
          {ticketId}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 12, top: 12 }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={26} sx={{ color: 'var(--sage)' }} />
          </Box>
        )}

        {!isLoading && ticket && (
          <>
            {/* Status row */}
            <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1} sx={{ mb: 2 }}>
              <Pill tone={STATUS_TONE[stageOf(ticket)] || STATUS_TONE.open} />
              <Pill tone={{ bg: 'var(--cream)', fg: 'var(--stone)', label: CATEGORY_LABEL[ticket.category] || ticket.category }} />
              <Pill tone={{
                bg: 'var(--cream)',
                fg: 'var(--stone)',
                label: `${(ticket.priority || 'normal').toUpperCase()} PRIORITY`,
              }} />
            </Stack>

            {/* Where it stands, as the desk tracks it */}
            <Box sx={{ border: '1px solid var(--border-l)', borderRadius: '12px', px: 2, pt: 2, pb: 1.5, mb: 2 }}>
              <Stepper stage={stageOf(ticket)} />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                <Fact label="Raised" value={formatDateTime(ticket.createdAt)} />
                <Fact label="Department" value={ticket.department?.name} />
                <Fact label="Ticket" value={ticket.ticket_code ? `${ticket.ticket_code}${ticket.ticket_status ? ` · ${ticket.ticket_status}` : ''}` : ''} />
                {stageOf(ticket) === 'closed'
                  ? <Fact label="Closed" value={formatDateTime(ticket.closed_at)} />
                  : <Fact label="Resolved" value={ticket.resolved_at ? formatDateTime(ticket.resolved_at) : ''} />}
              </Box>
              <Typography sx={{ fontSize: 12, color: 'var(--stone)', mt: 1.25 }}>
                {stageOf(ticket) === 'open' && 'Received by our Customer Service Desk. It will be handed to the right department shortly.'}
                {stageOf(ticket) === 'assigned' && `With ${ticket.department?.name || 'the department'}. They are looking into it.`}
                {stageOf(ticket) === 'in_progress' && `${ticket.department?.name || 'The department'} is working on it. Replies from the desk appear below.`}
                {stageOf(ticket) === 'resolved' && 'Resolved. If something is still wrong, reply below and the desk will pick it up.'}
                {stageOf(ticket) === 'closed' && 'This ticket is closed. Raise a new one if you need more help.'}
              </Typography>
            </Box>

            {/* A question from the team, waiting for the customer's answer */}
            {ticket.info_request && !ticket.info_request.answered && ticket.status !== 'closed' && (
              <Alert severity="warning" sx={{ mb: 2, borderRadius: '12px', '& .MuiAlert-message': { width: '100%' } }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, mb: 0.5 }}>
                  We need a little more information · {formatDateTime(ticket.info_request.at)}
                </Typography>
                <Typography sx={{ fontSize: 13, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                  {ticket.info_request.text}
                </Typography>
                <Typography sx={{ fontSize: 12, mt: 0.75 }}>
                  Reply below and it goes straight to the team handling your ticket.
                </Typography>
              </Alert>
            )}

            {/* Original message */}
            <Box sx={{ bgcolor: 'var(--cream)', borderRadius: '12px', p: 2, mb: 2 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--stone)', textTransform: 'uppercase', mb: 0.5 }}>
                You · {formatDateTime(ticket.createdAt)}
              </Typography>
              <Typography sx={{ fontSize: 13.5, color: 'var(--ink)', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                {ticket.message}
              </Typography>
            </Box>

            {/* Corrective action — what the lab did about it, reported by
                Quality. Shown as a summary, not as a chat message. */}
            {ticket.corrective_action && (
              <Box sx={{ bgcolor: 'var(--mint, #EEF8F2)', border: '1px solid var(--sage-p)', borderRadius: '12px', p: 2, mb: 2 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--stone)', textTransform: 'uppercase', mb: 0.75 }}>
                  Corrective action{ticket.corrective_action.at ? ` · ${formatDateTime(ticket.corrective_action.at)}` : ''}
                </Typography>
                {(ticket.corrective_action.actions || []).length > 0 && (
                  <Box component="ul" sx={{ m: 0, mb: ticket.corrective_action.summary ? 1 : 0, pl: 2.5 }}>
                    {ticket.corrective_action.actions.map((a, i) => (
                      <Typography key={i} component="li" sx={{ fontSize: 13.5, color: 'var(--ink)', lineHeight: 1.6 }}>{a}</Typography>
                    ))}
                  </Box>
                )}
                {ticket.corrective_action.amended_report && (
                  <Typography sx={{ fontSize: 12.5, color: 'var(--stone)', mb: 0.75 }}>
                    Amended report: <b>{ticket.corrective_action.amended_report}</b>
                  </Typography>
                )}
                {ticket.corrective_action.summary && (
                  <Typography sx={{ fontSize: 13.5, color: 'var(--ink)', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                    {ticket.corrective_action.summary}
                  </Typography>
                )}
                {ticket.corrective_action.verified_by && (
                  <Typography sx={{ fontSize: 12, color: 'var(--stone)', mt: 1 }}>
                    Verified by <b>{ticket.corrective_action.verified_by}</b>
                    {ticket.corrective_action.date ? ` · ${ticket.corrective_action.date}` : ''}
                  </Typography>
                )}
              </Box>
            )}

            {/* Thread */}
            {(ticket.replies?.length || 0) > 0 && (
              <Stack spacing={1.75} sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--stone)', textTransform: 'uppercase' }}>
                  Conversation
                </Typography>
                {ticket.replies.map((r, idx) => {
                  const isAdmin = r.author_type === 'admin';
                  return (
                    <Stack key={r._id || idx} direction="row" spacing={1.5} alignItems="flex-start">
                      <Avatar sx={{
                        bgcolor: isAdmin ? 'var(--sage)' : '#1D4ED8',
                        width: 32, height: 32, fontSize: 12, fontWeight: 700, flexShrink: 0,
                      }}>
                        {isAdmin ? '🛟' : (r.author_name?.[0] || 'U')}
                      </Avatar>
                      <Box
                        sx={{
                          flex: 1, minWidth: 0,
                          bgcolor: isAdmin ? 'var(--mint, #EEF8F2)' : 'var(--cream)',
                          border: isAdmin ? '1px solid var(--sage-p)' : '1px solid var(--border-l)',
                          borderRadius: '12px',
                          p: 1.5,
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink)' }}>
                            {isAdmin ? 'TrustLab Support' : (r.author_name || 'You')}
                          </Typography>
                          <Typography sx={{ fontSize: 10.5, color: 'var(--stone)' }}>
                            {formatDateTime(r.createdAt)}
                          </Typography>
                        </Stack>
                        <Typography sx={{ fontSize: 13, color: 'var(--ink)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                          {r.message}
                        </Typography>
                      </Box>
                    </Stack>
                  );
                })}
              </Stack>
            )}

            {/* Reply box */}
            {ticket.status === 'closed' ? (
              <Alert severity="info" sx={{ mt: 2, borderRadius: '10px' }}>
                This ticket is closed. Please raise a new one if you need more help.
              </Alert>
            ) : (
              <Box sx={{ mt: 2 }}>
                {err && <Alert severity="error" sx={{ mb: 1.5, borderRadius: '10px' }}>{err}</Alert>}
                <Typography sx={{ fontSize: 12, color: 'var(--stone)', mb: 1 }}>
                  Your reply goes to our Customer Service Desk, who will pass it to the department handling your ticket.
                </Typography>
                <TextField
                  fullWidth multiline rows={3}
                  placeholder="Write to the desk…"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  disabled={sending}
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>

      {!isLoading && ticket && ticket.status !== 'closed' && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onSend}
            disabled={sending || !text.trim()}
            startIcon={sending ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SendOutlinedIcon />}
            sx={{ borderRadius: 100, textTransform: 'none', fontWeight: 700, px: 2.5 }}
          >
            {sending ? 'Sending…' : 'Send reply'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

// ───────── Page ─────────
const MyTickets = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { data, isLoading } = useListMyTicketsQuery(undefined, { skip: !isAuthenticated });
  const tickets = data?.tickets || [];

  const [openId, setOpenId] = useState(searchParams.get('open') || null);

  // Auth gate — bounce guests to login with ?next=/my-tickets
  useEffect(() => {
    if (!isAuthenticated) {
      navigate(`${ROUTES.LOGIN}?next=${encodeURIComponent(ROUTES.MY_TICKETS)}`, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  if (!isAuthenticated) return null;

  const closeDialog = () => {
    setOpenId(null);
    if (searchParams.get('open')) {
      const next = new URLSearchParams(searchParams);
      next.delete('open');
      setSearchParams(next, { replace: true });
    }
  };

  return (
    <Box sx={{ bgcolor: 'var(--cream)', minHeight: '90vh', py: { xs: 5, md: 7 }, px: { xs: 2, md: 4 } }}>
      <Box sx={{ width: '100%', maxWidth: 820, mx: 'auto' }}>
        {/* Header */}
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'flex-end' }} spacing={1.5} sx={{ mb: 3 }}>
          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--sage)', mb: 1 }}>
              Support
            </Typography>
            <Typography className="serif" sx={{ fontSize: { xs: 26, md: 32 }, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.15 }}>
              My tickets
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'var(--stone)', mt: 0.5 }}>
              Track replies from our support team
            </Typography>
          </Box>
          <Button
            variant="contained" color="primary"
            onClick={() => navigate(ROUTES.SUPPORT)}
            sx={{ borderRadius: 100, textTransform: 'none', fontWeight: 700 }}
          >
            + New ticket
          </Button>
        </Stack>

        {/* List */}
        {isLoading ? (
          <MyTicketsShimmer count={4} />
        ) : tickets.length === 0 ? (
          <Box
            sx={{
              bgcolor: '#fff', border: '1px solid var(--border-l)', borderRadius: '16px',
              p: 5, textAlign: 'center',
            }}
          >
            <Box sx={{
              width: 64, height: 64, borderRadius: '50%',
              bgcolor: 'var(--mint, #EEF8F2)', border: '1.5px solid var(--sage-p)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', mb: 2,
            }}>
              <SupportAgentOutlinedIcon sx={{ fontSize: 30, color: 'var(--sage)' }} />
            </Box>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)', mb: 0.75 }}>
              No tickets yet
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'var(--stone)', mb: 2.5, maxWidth: 360, mx: 'auto' }}>
              When you raise a complaint or query, you'll see the full conversation here.
            </Typography>
            <Button
              variant="contained" color="primary"
              onClick={() => navigate(ROUTES.SUPPORT)}
              sx={{ borderRadius: 100, textTransform: 'none', fontWeight: 700 }}
            >
              Raise a ticket
            </Button>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {tickets.map((t) => {
              const repliesCount = t.replies?.length || 0;
              const lastReply = repliesCount ? t.replies[repliesCount - 1] : null;
              const adminUnread = repliesCount > 0 && lastReply.author_type === 'admin';
              return (
                <Box
                  key={t.ticket_id}
                  onClick={() => setOpenId(t.ticket_id)}
                  sx={{
                    bgcolor: '#fff',
                    border: adminUnread ? '1.5px solid var(--sage-p)' : '1px solid var(--border-l)',
                    borderRadius: '14px',
                    p: 2.25,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    '&:hover': { borderColor: 'var(--sage)', boxShadow: '0 8px 24px rgba(21,42,30,0.06)' },
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5} sx={{ mb: 1 }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--sage)', fontFamily: "'JetBrains Mono', monospace", mb: 0.25 }}>
                        {t.ticket_id}
                      </Typography>
                      <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.4 }} noWrap>
                        {t.message.slice(0, 100)}{t.message.length > 100 ? '…' : ''}
                      </Typography>
                    </Box>
                    <Pill tone={STATUS_TONE[stageOf(t)] || STATUS_TONE.open} />
                  </Stack>
                  <Stack direction="row" spacing={2} flexWrap="wrap" rowGap={0.5} sx={{ fontSize: 11.5, color: 'var(--stone)' }}>
                    <Typography component="span" sx={{ fontSize: 11.5 }}>
                      📂 {CATEGORY_LABEL[t.category] || t.category}
                    </Typography>
                    <Typography component="span" sx={{ fontSize: 11.5 }}>
                      📅 {formatDateTime(t.createdAt)}
                    </Typography>
                    <Typography component="span" sx={{ fontSize: 11.5 }}>
                      💬 {repliesCount} {repliesCount === 1 ? 'reply' : 'replies'}
                    </Typography>
                    {t.department?.name && (
                      <Typography component="span" sx={{ fontSize: 11.5 }}>
                        🏢 {t.department.name}
                      </Typography>
                    )}
                    {t.ticket_code && (
                      <Typography component="span" sx={{ fontSize: 11.5, fontFamily: "'JetBrains Mono', monospace" }}>
                        🎫 {t.ticket_code}
                      </Typography>
                    )}
                    {t.info_request && !t.info_request.answered && t.status !== 'closed' && (
                      <Box
                        component="span"
                        sx={{
                          fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
                          bgcolor: '#FEF3C7', color: '#92400E',
                          px: 0.875, py: 0.25, borderRadius: 100,
                        }}
                      >
                        INFORMATION NEEDED
                      </Box>
                    )}
                    {t.corrective_action && (
                      <Box
                        component="span"
                        sx={{
                          fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
                          bgcolor: '#D1FAE5', color: '#065F46',
                          px: 0.875, py: 0.25, borderRadius: 100,
                        }}
                      >
                        CORRECTIVE ACTION RECORDED
                      </Box>
                    )}
                    {adminUnread && (
                      <Box
                        component="span"
                        sx={{
                          fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
                          bgcolor: 'var(--sage)', color: '#fff',
                          px: 0.875, py: 0.25, borderRadius: 100,
                        }}
                      >
                        NEW REPLY
                      </Box>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>

      <TicketDialog ticketId={openId} open={!!openId} onClose={closeDialog} />
    </Box>
  );
};

export default MyTickets;
